import React, { useCallback, useEffect, useMemo, useState } from "react";
import { killSwitchApi } from "../api/routes";
import { Button, Card, Pill, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const toneFor = (enabled) => (enabled ? "success" : "danger");

function StatusPill({ enabled, label }) {
  const style =
    toneFor(enabled) === "success"
      ? { background: "rgba(25,164,99,0.12)", color: "#0c3b2e" }
      : { background: "rgba(220,38,38,0.12)", color: "#991b1b" };
  return <span style={{ ...style, borderRadius: 999, padding: "4px 10px", fontWeight: 800, fontSize: 12 }}>{label}</span>;
}

export default function KillSwitchPanel() {
  const [panelState, setPanelState] = usePanelCache("killSwitch");
  const config = useMemo(
    () =>
      panelState.config || {
        globalKillSwitch: false,
        llmEnabled: true,
        smsEnabled: true,
      },
    [panelState.config]
  );
  const [draft, setDraft] = useState(config);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setDraft(config);
  }, [config]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    setLoading(true);
    try {
      const res = await killSwitchApi.get();
      setPanelState({ config: res.data });
      if (!quiet) {
        setToast({ tone: "success", message: "Kill switch loaded." });
      }
    } catch (e) {
      setToast({ tone: "error", message: e.message || "Failed to load kill switch." });
    } finally {
      setLoading(false);
    }
  }, [setPanelState]);

  useEffect(() => {
    load({ quiet: true });
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await killSwitchApi.update(draft);
      setPanelState({ config: res.data });
      setToast({ tone: "success", message: "Kill switch updated." });
    } catch (e) {
      setToast({ tone: "error", message: e.message || "Failed to update kill switch." });
    } finally {
      setSaving(false);
    }
  };

  const setBoolean = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <Card
        title="Kill Switch"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Button tone="ghost" onClick={load} loading={loading}>Refresh</Button>
            <Button onClick={save} loading={saving}>Save</Button>
          </div>
        }
      >
        <p style={{ marginTop: 0, color: "var(--stone-700)" }}>
          This row is enforced by the backend before any real LLM call and before SMS ingestion or outbound SMS work.
        </p>

        <div style={{ display: "grid", gap: 16 }}>
          <Card title="Global switch" tone="accent">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 800 }}>Global Kill Switch</div>
                <div style={{ color: "var(--stone-700)", marginTop: 4 }}>Stops LLM usage and SMS processing immediately.</div>
              </div>
              <StatusPill enabled={!draft.globalKillSwitch} label={draft.globalKillSwitch ? "ACTIVE" : "CLEAR"} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Button tone={draft.globalKillSwitch ? "secondary" : "ghost"} onClick={() => setBoolean("globalKillSwitch", true)}>
                Activate
              </Button>
              <Button tone={!draft.globalKillSwitch ? "secondary" : "ghost"} onClick={() => setBoolean("globalKillSwitch", false)}>
                Clear
              </Button>
            </div>
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            <Card title="LLM" tone="accent">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 800 }}>LLM provider calls</div>
                  <div style={{ color: "var(--stone-700)", marginTop: 4 }}>Blocks real OpenAI calls. Fake provider remains usable in tests.</div>
                </div>
                <StatusPill enabled={draft.llmEnabled} label={draft.llmEnabled ? "ENABLED" : "DISABLED"} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <Button tone={draft.llmEnabled ? "secondary" : "ghost"} onClick={() => setBoolean("llmEnabled", true)}>
                  Enable
                </Button>
                <Button tone={!draft.llmEnabled ? "secondary" : "ghost"} onClick={() => setBoolean("llmEnabled", false)}>
                  Disable
                </Button>
              </div>
            </Card>

            <Card title="SMS" tone="accent">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 800 }}>SMS processing</div>
                  <div style={{ color: "var(--stone-700)", marginTop: 4 }}>Stops new inbound SMS ingestion and skips outbound SMS work.</div>
                </div>
                <StatusPill enabled={draft.smsEnabled} label={draft.smsEnabled ? "ENABLED" : "DISABLED"} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <Button tone={draft.smsEnabled ? "secondary" : "ghost"} onClick={() => setBoolean("smsEnabled", true)}>
                  Enable
                </Button>
                <Button tone={!draft.smsEnabled ? "secondary" : "ghost"} onClick={() => setBoolean("smsEnabled", false)}>
                  Disable
                </Button>
              </div>
            </Card>
          </div>

          <Card title="Current persisted state">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <Pill>Global: {String(Boolean(config.globalKillSwitch))}</Pill>
              <Pill>LLM Enabled: {String(Boolean(config.llmEnabled))}</Pill>
              <Pill>SMS Enabled: {String(Boolean(config.smsEnabled))}</Pill>
            </div>
            <div style={{ color: "var(--stone-700)", fontSize: 14 }}>
              Updated at: {config.updatedAt ? new Date(config.updatedAt * 1000).toLocaleString() : "Never"}
            </div>
            <div style={{ color: "var(--stone-700)", fontSize: 14, marginTop: 4 }}>
              Updated by: {config.updatedBy || "Unknown"}
            </div>
          </Card>
        </div>
      </Card>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
